<!--
 * Path: packages/frontend/src/core/components/common/EntityIcon.vue
 * Task: Renders a visual avatar or icon for a given entity with typed parameters and custom shape support.
 * Fix: Forced `!important` on border-radius to prevent Vuetify's native <v-list-item> from overriding shapes.
-->
<template>
  <!-- Render Avatar with transparent background if shape is 'none' -->
  <v-avatar
    v-if="iconStyle.shape === 'none'"
    v-bind="iconAttrs"
    color="transparent"
    :size="parsedSize"
    rounded="0"
    class="entity-icon-avatar"
  >
    <v-icon :color="resolveHexColor(iconStyle.color)" :size="parsedSize">
      {{ iconStyle.icon }}
    </v-icon>
  </v-avatar>

  <!-- Render colored Avatar (Circle or Squircle) otherwise -->
  <v-avatar
    v-else
    v-bind="$attrs"
    :color="resolveHexColor(iconStyle.color)"
    :size="parsedSize"
    rounded="0"
    :style="`border-radius: ${iconStyle.shape === 'circle' ? '50%' : dynamicBorderRadius} !important;`"
    class="entity-icon-avatar"
  >
    <v-icon v-if="iconStyle.icon && iconStyle.icon.trim() !== ''" color="white" :size="iconSize">
      {{ iconStyle.icon }}
    </v-icon>
  </v-avatar>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { useAppStyle } from '@/core/composables/useAppStyle';
import type { Entity } from '@/core/types/editor';

// Prevents attributes from automatically falling through to the root element.
// This is crucial to intercept and remove box-shadows on transparent avatars.
defineOptions({
  inheritAttrs: false
});

type IconEntity = Partial<Entity> & {
  icon?: string;
  color?: string;
  iconShape?: string;
  entityType?: string;
};

interface Props {
  entity: IconEntity;
  size?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 32
});

const attrs = useAttrs();
const { getEntityStyle, resolveHexColor } = useAppStyle();

// Safely strip elevation and shadow classes for bare icons to prevent rendering ghost shadows
const iconAttrs = computed(() => {
  const { class: className, ...rest } = attrs;
  let safeClass = '';

  if (className) {
    safeClass = String(className)
      .replace(/elevation-\d+/g, '')
      .replace(/shadow-[a-z0-9-]+/g, '')
      .trim();
  }
  return { ...rest, class: safeClass };
});

const parsedSize = computed<number>(() => {
  const s = parseInt(String(props.size), 10);
  return isNaN(s) ? 32 : s;
});

const dynamicBorderRadius = computed<string>(() => `${parsedSize.value * 0.25}px`);
const iconSize = computed<number>(() => Math.floor(parsedSize.value * 0.60));

const iconStyle = computed<{ icon: string; color: string; shape: string }>(() => {
  let typeStyle: any = null;

  // If the entity doesn't provide all three explicit styles, we fetch the fallback from its type
  if (!props.entity.icon || !props.entity.color || !props.entity.iconShape) {
    typeStyle = getEntityStyle(props.entity.entityType || '');
  }

  return {
    icon: props.entity.icon || typeStyle?.icon || 'mdi-help-circle-outline',
    color: props.entity.color || typeStyle?.color || 'grey',
    shape: props.entity.iconShape || typeStyle?.iconShape || 'squircle'
  };
});
</script>

<style scoped>
.entity-icon-avatar {
  transition: transform 0.2s ease, border-radius 0.2s ease;
}
</style>
